import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import TextInputComponent from '../../../../components/input/TextInputComponent';
import WeeklyScheduleGrid from '../../access-level-designer/components/WeeklyScheduleGrid';
import SchedulePresets from '../../access-level-designer/components/SchedulePresets';
import {
  createEmptyWeeklySchedule,
  createAlwaysSchedule,
  createNeverSchedule,
  createWeekdaysSchedule,
  createBusinessHoursSchedule,
  computeScheduleType,
} from '../utils';

/**
 * Create/edit dialog for a Timetable (name + weekly schedule).
 * Pass `timetable` to edit an existing one, or omit/null to create a new one.
 */
export default function TimetableEditorDialog({ open, timetable, onClose, onSave }) {
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('custom');
  const [weeklySchedule, setWeeklySchedule] = useState(createEmptyWeeklySchedule());

  useEffect(() => {
    if (!open) return;
    if (timetable) {
      setName(timetable.name || '');
      setWeeklySchedule(timetable.weeklySchedule || createEmptyWeeklySchedule());
      setPreset('custom');
    } else {
      setName('');
      setWeeklySchedule(createEmptyWeeklySchedule());
      setPreset('custom');
    }
  }, [open, timetable]);

  const handlePresetChange = (value) => {
    setPreset(value);
    switch (value) {
      case 'always':
        setWeeklySchedule(createAlwaysSchedule());
        break;
      case 'never':
        setWeeklySchedule(createNeverSchedule());
        break;
      case 'weekdays':
        setWeeklySchedule(createWeekdaysSchedule());
        break;
      case 'business':
        setWeeklySchedule(createBusinessHoursSchedule());
        break;
      case 'custom':
      default:
        break;
    }
  };

  const handleScheduleChange = (newSchedule) => {
    setWeeklySchedule(newSchedule);
    setPreset('custom');
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      id: timetable?.id || uuidv4(),
      name: name.trim(),
      weeklySchedule,
      scheduleType: computeScheduleType(weeklySchedule),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{timetable ? 'Edit Timetable' : 'New Timetable'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <TextInputComponent
                value={name}
                setValue={setName}
                label="Timetable Name"
                placeholder="e.g., Business Hours, Night Shift"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SchedulePresets value={preset} onChange={handlePresetChange} />
            </Box>
          </Box>

          <WeeklyScheduleGrid schedule={weeklySchedule} onChange={handleScheduleChange} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!name.trim()}>
          Save Timetable
        </Button>
      </DialogActions>
    </Dialog>
  );
}
